import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../../core/components/Button.tsx';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import FieldPathInput from '../components/FieldPathInput.tsx';
import NestedActionList from '../components/NestedActionList.tsx';
import { useActionUpdate } from '../hooks/useActionUpdate.ts';
import type {
    ConditionalAction,
    ActionType,
    Condition,
    ConditionOperator,
    FieldType,
    AliasContextEntry,
} from '../types.ts';
import { createDefaultAction } from '../utils/actionDefaults.ts';

const PlusIcon = withIcon(faPlus);

interface ConditionalBlockProps {
    action: ConditionalAction;
    mappingIndex: number;
    actionIndex: number;
    aliasContext?: AliasContextEntry[];
    onUpdate?: (updates: Partial<ConditionalAction>) => void;
}

const ConditionEditor = ({
    condition,
    onChange,
    conditionOperators,
    fieldTypes,
}: {
    condition: Condition;
    onChange: (condition: Condition) => void;
    conditionOperators: { value: string; label: string }[];
    fieldTypes: { value: string; label: string }[];
}) => {
    const { t } = useTranslation('integrations');
    const needsValue = !['is_null', 'is_not_null'].includes(condition.operator);

    return (
        <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="text-xs text-content-weak mb-1 block">
                        {t('outputMapper.visualEditor.operator')}
                    </label>
                    <SelectField
                        value={condition.operator}
                        onValueChange={(op) => onChange({ ...condition, operator: op as ConditionOperator })}
                        options={conditionOperators}
                    />
                </div>
                {needsValue && (
                    <div>
                        <label className="text-xs text-content-weak mb-1 block">
                            {t('outputMapper.visualEditor.valueType')}
                        </label>
                        <SelectField
                            value={condition.value_type || 'string'}
                            onValueChange={(type) => onChange({ ...condition, value_type: type as FieldType })}
                            options={fieldTypes}
                        />
                    </div>
                )}
            </div>
            {needsValue && (
                <div>
                    <label className="text-xs text-content-weak mb-1 block">
                        {t('outputMapper.visualEditor.compareValue')}
                    </label>
                    <StringField
                        value={String(condition.value ?? '')}
                        onValueChange={(value) => onChange({ ...condition, value })}
                        placeholder={t('outputMapper.visualEditor.enterValue')}
                    />
                </div>
            )}
        </div>
    );
};

const ConditionalBlock = ({
    action,
    mappingIndex,
    actionIndex,
    aliasContext = [],
    onUpdate,
}: ConditionalBlockProps) => {
    const { t } = useTranslation('integrations');
    const { availableActionTypes, conditionOperators, fieldTypes } = useOutputMapperEditor();
    const handleUpdate = useActionUpdate<ConditionalAction>(mappingIndex, actionIndex, onUpdate);
    const [showElse, setShowElse] = useState(Boolean(action.false_actions?.length));

    const handleAddTrueAction = (actionType: ActionType) => {
        handleUpdate({ true_actions: [...action.true_actions, createDefaultAction(actionType)] });
    };

    const handleAddFalseAction = (actionType: ActionType) => {
        handleUpdate({ false_actions: [...(action.false_actions || []), createDefaultAction(actionType)] });
        setShowElse(true);
    };

    const toggleElse = () => {
        if (showElse) handleUpdate({ false_actions: undefined });
        setShowElse(!showElse);
    };

    const simpleCondition = 'operator' in action.condition ? action.condition : null;
    const keyBase = `${mappingIndex}-${actionIndex}`;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <label className="text-xs text-content-weak mb-1 block">{t('outputMapper.visualEditor.ifField')}</label>
                <FieldPathInput
                    value={action.source}
                    onChange={(source) => handleUpdate({ source })}
                    aliasContext={aliasContext}
                    placeholder={t('outputMapper.visualEditor.selectSourceField')}
                />
            </div>

            {simpleCondition && (
                <ConditionEditor
                    condition={simpleCondition}
                    onChange={(condition) => handleUpdate({ condition })}
                    conditionOperators={conditionOperators}
                    fieldTypes={fieldTypes}
                />
            )}

            <div>
                <div className="text-xs font-medium text-content-strong mb-2 bg-status-success-weak px-2 py-1 rounded">
                    {t('outputMapper.visualEditor.then')}
                </div>
                <NestedActionList
                    actions={action.true_actions}
                    mappingIndex={mappingIndex}
                    keyPrefix={`${keyBase}-true`}
                    aliasContext={aliasContext}
                    availableActionTypes={availableActionTypes}
                    onUpdateActions={(actions) => handleUpdate({ true_actions: actions })}
                />
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-medium text-content-strong bg-surface-medium px-2 py-1 rounded">
                        {t('outputMapper.visualEditor.else')}
                    </div>
                    {!showElse && (
                        <Button variant="ghost" type="button" className="text-xs" onClick={toggleElse}>
                            <PlusIcon className="w-3 h-3 mr-1" />
                            {t('outputMapper.visualEditor.addElse')}
                        </Button>
                    )}
                </div>
                {showElse && (
                    <NestedActionList
                        actions={action.false_actions || []}
                        mappingIndex={mappingIndex}
                        keyPrefix={`${keyBase}-false`}
                        aliasContext={aliasContext}
                        availableActionTypes={availableActionTypes}
                        onUpdateActions={(actions) => handleUpdate({ false_actions: actions })}
                    />
                )}
            </div>
        </div>
    );
};

export default ConditionalBlock;
